import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TrainHubLayout from './TrainHubLayout';
import {
  userService,
  auth,
  studentService,
  batchService,
  courseService,
  enrollmentService,
  attendanceService,
  paymentService,
} from '../services';
import { getStudentTypeLabel, getProgramLabel, getBatchLabel } from '../utils/studentDisplay';
import { computeAllBalances } from '../utils/paymentSummary';

const Dashboard = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({
    students: 0,
    courses: 0,
    enrollments: 0,
    attendance: 0,
    paidStudents: 0,
    unpaidStudents: 0,
    secondaryBatch: 0,
    professionalBatch: 0,
  });
  const [runningBatches, setRunningBatches] = useState([]);
  const [batchStats, setBatchStats] = useState({});
  const [recentStudents, setRecentStudents] = useState([]);
  const [recentEnrollments, setRecentEnrollments] = useState([]);
  const [paidStudents, setPaidStudents] = useState([]);

  useEffect(() => {
    if (auth.isAuthenticated()) {
      setCurrentUser(auth.getUser());
      loadUsers();
      loadDashboardCounts();
    }
  }, []);

  const loadDashboardCounts = async () => {
    try {
      const [students, courses, enrollments, attendance, paymentsResult, balancesResult, running] = await Promise.all([
        studentService.getAllStudents(),
        courseService.getAllCourses(),
        enrollmentService.getAllEnrollments(),
        attendanceService.getAllAttendance(),
        paymentService.getAll().catch(() => []),
        paymentService.getBalances().catch(() => []),
        batchService.getRunningBatches().catch(() => []),
      ]);
      const payments = Array.isArray(paymentsResult) ? paymentsResult : [];
      const balances = Array.isArray(balancesResult) && balancesResult.length > 0
        ? balancesResult
        : computeAllBalances(
            Array.isArray(students) ? students : [],
            payments
          );
      const studentList = Array.isArray(students) ? students : [];
      const runningList = Array.isArray(running) ? running : [];
      setRunningBatches(runningList);

      const statsEntries = await Promise.all(
        runningList.map(async (b) => {
          try {
            const stats = await batchService.getBatchStats(b._id);
            return [b._id, stats];
          } catch {
            return [b._id, { total: 0, byProgram: [] }];
          }
        })
      );
      const statsById = Object.fromEntries(statsEntries);
      setBatchStats(statsById);

      const secondaryRunning = runningList.find((b) => b.track === 'secondary');
      const professionalRunning = runningList.find((b) => b.track === 'professional');

      setCounts({
        students: studentList.length,
        courses: Array.isArray(courses) ? courses.length : 0,
        enrollments: Array.isArray(enrollments) ? enrollments.length : 0,
        attendance: Array.isArray(attendance) ? attendance.length : 0,
        paidStudents: balances.filter((b) => b.status === 'paid').length,
        unpaidStudents: balances.filter((b) => b.balance > 0).length,
        secondaryBatch: secondaryRunning ? statsById[secondaryRunning._id]?.total ?? 0 : 0,
        professionalBatch: professionalRunning ? statsById[professionalRunning._id]?.total ?? 0 : 0,
      });

      const sortedStudents = [...studentList].sort(
        (a, b) => new Date(b.registrationDate || b.createdAt || 0) - new Date(a.registrationDate || a.createdAt || 0)
      );
      setRecentStudents(sortedStudents.slice(0, 6));
      if (Array.isArray(enrollments)) {
        const sorted = [...enrollments].sort((a, b) => new Date(b.enrollmentDate || 0) - new Date(a.enrollmentDate || 0));
        setRecentEnrollments(sorted.slice(0, 6));
      }
      const seen = new Set();
      const unique = [];
      for (const b of balances.filter((bal) => bal.totalPaid > 0)) {
        if (!seen.has(b.studentId)) {
          seen.add(b.studentId);
          const student = studentList.find((s) => String(s._id) === b.studentId);
          const payment = payments.find((p) => String(p.student?._id || p.student) === b.studentId);
          unique.push({ _id: b.studentId, student: student || payment?.student, payment, summary: b });
        }
      }
      unique.sort((a, b) => new Date(b.payment?.createdAt || 0) - new Date(a.payment?.createdAt || 0));
      setPaidStudents(unique);
    } catch (error) {
      console.error('Error loading dashboard counts:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await userService.getAllUsers();
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStudentName = e => (!e.student ? '—' : typeof e.student === 'object' ? e.student.fullName : '—');
  const getPaidStudentName = item => (!item.student ? '—' : typeof item.student === 'object' ? item.student.fullName : '—');
  const getPaidStudentEmail = item => (item.student && typeof item.student === 'object' ? item.student.email || '—' : '—');
  const getPaymentMethod = item => {
    const p = item.payment;
    if (!p) return '—';
    if (p.method === 'office') return 'Office';
    if (p.method === 'bank_transfer' || p.receiptPath) return 'Bank Transfer';
    if (p.method === 'mobile_money' || p.paymentProof) return 'Mobile Money';
    return '—';
  };
  const getPaymentStatusLabel = (item) => {
    if (item.summary?.status === 'partial') return 'Partial';
    if (item.summary?.status === 'paid') return 'Paid in full';
    return getPaymentMethod(item);
  };
  const getCourseName = e => (!e.course ? '—' : typeof e.course === 'object' ? e.course.courseName : '—');
  const getStatus = e => {
    const date = e.enrollmentDate ? new Date(e.enrollmentDate) : null;
    if (!date) return { label: 'Enrolled', type: 'enrolled' };
    const daysAgo = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24);
    return daysAgo <= 7 ? { label: 'New', type: 'new' } : { label: 'Enrolled', type: 'enrolled' };
  };

  const now = new Date();
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const calDays = Array.from({ length: 5 }, (_, i) => {
    const d = new Date(now);
    d.setDate(now.getDate() - 2 + i);
    return { num: d.getDate(), label: days[d.getDay()], isToday: i === 2 };
  });

  const statsCards = [
    { icon: '👨‍🎓', label: 'Students', count: counts.students, bgColor: '#dbeafe', nav: '/admin/students' },
    { icon: '🎓', label: 'Secondary cohort', count: counts.secondaryBatch, bgColor: '#e0e7ff', nav: '/admin/batches' },
    { icon: '💼', label: 'Professional cohort', count: counts.professionalBatch, bgColor: '#cffafe', nav: '/admin/batches' },
    { icon: '📚', label: 'Courses', count: counts.courses, bgColor: '#fef3c7', nav: '/admin/courses' },
    { icon: '💰', label: 'Paid', count: counts.paidStudents, bgColor: '#fce7f3', nav: '/admin/payments' },
    { icon: '⏳', label: 'Awaiting pay', count: counts.unpaidStudents, bgColor: '#fff7ed', nav: '/admin/payments' },
  ];

  return (
    <TrainHubLayout>
      <style>{`
        /* ─── Dashboard professional styling ─── */
        .db-page {
          padding: 24px;
          display: flex;
          flex-direction: column;
          gap: 32px;
          min-height: 100%;
          background: var(--bg);
        }

        /* ─── HERO SECTION ─── */
        .db-hero {
          background: var(--surface);
          border-radius: 12px;
          padding: 32px;
          border: 1px solid var(--border);
          box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
        }
        .db-hero-text { 
          flex: 1; 
        }
        .db-hero-eyebrow {
          display: inline-block;
          background: #1e40af;
          color: white;
          border-radius: 16px;
          padding: 6px 16px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.025em;
          margin-bottom: 16px;
        }
        .db-hero-title {
          font-family: system-ui, -apple-system, sans-serif;
          font-size: 32px;
          font-weight: 700;
          line-height: 1.2;
          margin-bottom: 12px;
          color: var(--text);
        }
        .db-hero-title span {
          color: #1e40af;
        }
        .db-hero-sub {
          font-size: 16px;
          color: #4b5563;
          line-height: 1.6;
          max-width: 400px;
          margin-bottom: 24px;
        }
        .db-hero-actions { 
          display: flex; 
          gap: 12px; 
          flex-wrap: wrap; 
        }
        .db-btn-primary {
          padding: 12px 24px;
          background: #1e40af;
          color: #ffffff;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          font-family: inherit;
          transition: background-color 0.2s;
        }
        .db-btn-primary:hover { 
          background: #1e3a8a;
        }
        .db-btn-ghost {
          padding: 12px 24px;
          background: transparent;
          color: #4b5563;
          border: 1px solid var(--border-md);
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          font-family: inherit;
          transition: all 0.2s;
        }
        .db-btn-ghost:hover { 
          background: var(--bg);
          border-color: #9ca3af;
        }

        .db-hero-visual {
          width: 80px;
          height: 80px;
          background: #1e40af;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          flex-shrink: 0;
        }

        /* ─── STATS GRID ─── */
        .db-stats-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 20px;
        }
        .db-cohort-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }
        .db-cohort-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1);
        }
        .db-cohort-card.running { border-color: #86efac; }
        .db-cohort-card.missing { border-color: #fcd34d; background: #fffbeb; }
        .db-cohort-title { font-size: 16px; font-weight: 700; margin-bottom: 4px; }
        .db-cohort-meta { font-size: 13px; color: var(--muted); margin-bottom: 12px; }
        .db-cohort-count { font-size: 28px; font-weight: 700; color: #1e40af; }
        .db-cohort-programs { margin-top: 12px; display: flex; flex-wrap: wrap; gap: 6px; }
        .db-cohort-chip {
          font-size: 11px;
          padding: 4px 10px;
          background: var(--bg);
          border-radius: 12px;
          color: var(--text2);
        }
        .db-stat-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 24px;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1);
        }
        .db-stat-card:hover {
          transform: translateY(-2px);
          border-color: #d1d5db;
          box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
        }
        .db-stat-icon-wrap {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          background: var(--bg-color);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          margin-bottom: 16px;
        }
        .db-stat-num {
          font-family: inherit;
          font-size: 28px;
          font-weight: 700;
          line-height: 1;
          margin-bottom: 8px;
          color: var(--text);
        }
        .db-stat-label {
          font-size: 14px;
          font-weight: 500;
          color: var(--muted);
        }

        /* ─── SECTION HEADER ─── */
        .db-section-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }
        .db-section-title {
          font-family: inherit;
          font-size: 18px;
          font-weight: 600;
          color: var(--text);
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .db-section-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #1e40af;
        }
        .db-view-all {
          background: transparent;
          border: 1px solid var(--border-md);
          color: #1e40af;
          font-size: 13px;
          font-weight: 500;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-family: inherit;
          transition: all 0.2s;
        }
        .db-view-all:hover {
          background: var(--bg);
          border-color: #9ca3af;
        }

        /* ─── TABLE ─── */
        .db-table-wrap {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1);
        }
        .db-table-scroll {
          overflow-x: auto;
        }
        .db-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 500px;
        }
        .db-table thead {
          background: var(--bg);
        }
        .db-table th {
          text-align: left;
          padding: 16px 20px;
          font-size: 12px;
          font-weight: 600;
          color: var(--text2);
          text-transform: uppercase;
          letter-spacing: 0.025em;
          border-bottom: 1px solid var(--border);
          white-space: nowrap;
        }
        .db-table td {
          padding: 16px 20px;
          font-size: 14px;
          color: var(--text);
          border-bottom: 1px solid #f3f4f6;
          vertical-align: middle;
        }
        .db-table tbody tr {
          transition: background 0.15s;
        }
        .db-table tbody tr:hover {
          background: var(--bg);
        }
        .db-table tbody tr:last-child td {
          border-bottom: none;
        }

        .db-student-cell {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .db-mini-avatar {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          background: #1e40af;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 600;
          color: white;
          font-family: inherit;
          flex-shrink: 0;
        }

        .db-badge {
          display: inline-flex;
          align-items: center;
          padding: 4px 12px;
          border-radius: 16px;
          font-size: 12px;
          font-weight: 500;
          white-space: nowrap;
        }
        .db-badge-blue {
          background: #dbeafe;
          color: #1e40af;
        }
        .db-badge-new {
          background: #fce7f3;
          color: #be185d;
        }
        .db-badge-green {
          background: #d1fae5;
          color: #059669;
        }
        .db-badge-gold {
          background: #fef3c7;
          color: #d97706;
        }

        .db-action-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: transparent;
          border: 1px solid var(--border-md);
          border-radius: 6px;
          color: var(--text2);
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          font-family: inherit;
        }
        .db-action-btn:hover {
          background: var(--bg);
          border-color: #9ca3af;
          color: #1e40af;
        }

        .db-empty {
          text-align: center;
          padding: 48px 24px;
          color: var(--muted);
          font-size: 14px;
        }
        .db-empty-icon {
          font-size: 36px;
          display: block;
          margin-bottom: 12px;
          opacity: 0.5;
        }

        /* ─── TWO-COL LAYOUT ─── */
        .db-two-col {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 24px;
          align-items: start;
        }

        /* ─── WIDGETS (right column cards) ─── */
        .db-widget {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1);
        }

        /* ─── CALENDAR ─── */
        .db-cal-days {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 8px;
        }
        .db-cal-day {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 12px 6px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          border: 1px solid transparent;
        }
        .db-cal-day:hover {
          background: var(--bg);
        }
        .db-cal-day.today {
          background: #dbeafe;
          border-color: #1e40af;
        }
        .db-cal-num {
          font-family: inherit;
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 4px;
          color: var(--text);
        }
        .db-cal-day.today .db-cal-num {
          color: #1e40af;
        }
        .db-cal-lbl {
          font-size: 11px;
          color: var(--muted);
          font-weight: 500;
          text-transform: uppercase;
        }

        /* ─── PEOPLE LIST ─── */
        .db-people-list {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .db-person-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          border-radius: 8px;
          transition: background 0.15s;
          cursor: default;
        }
        .db-person-item:hover {
          background: var(--bg);
        }
        .db-person-av {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          flex-shrink: 0;
          background: #ea580c;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 14px;
          color: white;
          font-family: inherit;
        }
        .db-person-info {
          flex: 1;
        }
        .db-person-name {
          font-size: 14px;
          font-weight: 500;
          color: var(--text);
          margin-bottom: 2px;
        }
        .db-person-role {
          font-size: 12px;
          color: var(--muted);
        }
        .db-person-actions {
          display: flex;
          gap: 6px;
        }
        .db-tiny-btn {
          width: 32px;
          height: 32px;
          background: transparent;
          border: 1px solid var(--border-md);
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .db-tiny-btn:hover {
          background: var(--bg);
          border-color: #9ca3af;
        }

        /* ─── UPCOMING ─── */
        .db-upcoming-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          border-radius: 8px;
          transition: background 0.15s;
        }
        .db-upcoming-item:hover {
          background: var(--bg);
        }
        .db-upcoming-av {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          background: #ec4899;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 600;
          color: white;
          font-family: inherit;
          flex-shrink: 0;
        }
        .db-today-chip {
          margin-left: auto;
          padding: 4px 12px;
          background: #d1fae5;
          color: #059669;
          border-radius: 16px;
          font-size: 11px;
          font-weight: 500;
        }

        /* ─── LOADING DOTS ─── */
        .db-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px;
          gap: 8px;
        }
        .db-loading-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #1e40af;
          animation: dbPulse 1.2s ease-in-out infinite;
        }
        .db-loading-dot:nth-child(2) {
          animation-delay: 0.2s;
          background: #7c3aed;
        }
        .db-loading-dot:nth-child(3) {
          animation-delay: 0.4s;
          background: #dc2626;
        }
        @keyframes dbPulse {
          0%, 80%, 100% {
            transform: scale(0.6);
            opacity: 0.4;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }

        /* ─── RESPONSIVE ─── */
        @media (max-width: 1300px) {
          .db-stats-grid {
            grid-template-columns: repeat(3, 1fr);
          }
          .db-cohort-grid {
            grid-template-columns: 1fr;
          }
          .db-two-col {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 768px) {
          .db-page {
            padding: 16px;
            gap: 24px;
          }
          .db-stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .db-hero {
            padding: 24px;
          }
          .db-hero-title {
            font-size: 28px;
          }
          .db-hero-visual {
            width: 64px;
            height: 64px;
          }
        }
        @media (max-width: 480px) {
          .db-hero {
            flex-direction: column;
            text-align: center;
            padding: 20px;
          }
          .db-hero-sub {
            max-width: 100%;
          }
          .db-hero-actions {
            justify-content: center;
          }
          .db-hero-visual {
            width: 56px;
            height: 56px;
            order: -1;
            font-size: 28px;
          }
          .db-stats-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="db-page">

        {/* ── Hero Banner ── */}
        <div className="db-hero">
          <div className="db-hero-text">
            <div className="db-hero-eyebrow">Training Center Dashboard</div>
            <h1 className="db-hero-title">
              Welcome back,<br />
              <span>{currentUser?.username || 'Administrator'}</span>
            </h1>
            <p className="db-hero-sub">
              Manage students, courses, and instructors from your central dashboard.
            </p>
            <div className="db-hero-actions">
              <button className="db-btn-primary" onClick={() => navigate('/admin/batches')}>Manage Batches</button>
              <button className="db-btn-ghost" onClick={() => navigate('/admin/students')}>View Students</button>
            </div>
          </div>
          <div className="db-hero-visual">
            🎓
          </div>
        </div>

        {/* ── Stats Cards ── */}
        <div>
          <div className="db-section-head">
            <div className="db-section-title">
              <span className="db-section-dot" />
              Center Overview
            </div>
          </div>
          <div className="db-stats-grid">
            {statsCards.map((card, i) => (
              <div
                key={i}
                className="db-stat-card"
                onClick={() => navigate(card.nav)}
              >
                <div className="db-stat-icon-wrap" style={{ '--bg-color': card.bgColor, background: card.bgColor }}>
                  {card.icon}
                </div>
                <div className="db-stat-num">{card.count}</div>
                <div className="db-stat-label">{card.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Running cohorts ── */}
        <div>
          <div className="db-section-head">
            <div className="db-section-title">
              <span className="db-section-dot" style={{ background: '#10B981' }} />
              Active Cohorts (Batches)
            </div>
            <button className="db-view-all" onClick={() => navigate('/admin/batches')}>Manage batches →</button>
          </div>
          <div className="db-cohort-grid">
            {['secondary', 'professional'].map((track) => {
              const batch = runningBatches.find((b) => b.track === track);
              const stats = batch ? batchStats[batch._id] : null;
              const label = track === 'secondary' ? 'Secondary' : 'Professional';
              const icon = track === 'secondary' ? '🎓' : '💼';
              return (
                <div
                  key={track}
                  className={`db-cohort-card ${batch ? 'running' : 'missing'}`}
                >
                  <div className="db-cohort-title">
                    {icon} {label}
                  </div>
                  {batch ? (
                    <>
                      <div className="db-cohort-meta">{batch.name}</div>
                      <div className="db-cohort-count">{stats?.total ?? 0} students</div>
                      {stats?.byProgram?.length > 0 && (
                        <div className="db-cohort-programs">
                          {stats.byProgram.map((p) => (
                            <span key={p.program} className="db-cohort-chip">
                              {p.program}: {p.count}
                            </span>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="db-cohort-meta">
                      No running batch — activate a batch before registration opens for {label.toLowerCase()} students.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Recent registrations ── */}
        <div>
          <div className="db-section-head">
            <div className="db-section-title">
              <span className="db-section-dot" style={{ background: '#3B82F6' }} />
              Recent Registrations
            </div>
            <button className="db-view-all" onClick={() => navigate('/admin/students')}>View all →</button>
          </div>
          <div className="db-table-wrap">
            <div className="db-table-scroll">
              <table className="db-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Type</th>
                    <th>Program</th>
                    <th>Batch</th>
                    <th>Registered</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="5">
                        <div className="db-loading">
                          <div className="db-loading-dot" />
                          <div className="db-loading-dot" />
                          <div className="db-loading-dot" />
                        </div>
                      </td>
                    </tr>
                  ) : recentStudents.length === 0 ? (
                    <tr>
                      <td colSpan="5">
                        <div className="db-empty">
                          <span className="db-empty-icon">👨‍🎓</span>
                          No students registered yet.
                        </div>
                      </td>
                    </tr>
                  ) : (
                    recentStudents.map((s, i) => (
                      <tr key={s._id || i}>
                        <td>
                          <div className="db-student-cell">
                            <div className="db-mini-avatar">
                              {(s.fullName?.[0] || '?').toUpperCase()}
                            </div>
                            <span style={{ fontWeight: 500 }}>{s.fullName}</span>
                          </div>
                        </td>
                        <td>
                          <span className="db-badge db-badge-blue">
                            {getStudentTypeLabel(s.studentType)}
                          </span>
                        </td>
                        <td style={{ color: '#4B5563' }}>{getProgramLabel(s)}</td>
                        <td style={{ color: '#4B5563' }}>{getBatchLabel(s)}</td>
                        <td style={{ color: '#4B5563', fontSize: '13px' }}>
                          {s.registrationDate
                            ? new Date(s.registrationDate).toLocaleDateString()
                            : '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── Paid Students Table ── */}
        <div>
          <div className="db-section-head">
            <div className="db-section-title">
              <span className="db-section-dot" style={{ background: '#F59E0B', boxShadow: '0 0 8px #F59E0B' }} />
              Paid Students
            </div>
            <button className="db-view-all" onClick={() => navigate('/admin/payments')}>View All →</button>
          </div>
          <div className="db-table-wrap">
            <div className="db-table-scroll">
              <table className="db-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Email</th>
                    <th>Method</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="4"><div className="db-loading"><div className="db-loading-dot"/><div className="db-loading-dot"/><div className="db-loading-dot"/></div></td></tr>
                  ) : paidStudents.length === 0 ? (
                    <tr><td colSpan="4"><div className="db-empty"><span className="db-empty-icon">💳</span>No paid students yet.</div></td></tr>
                  ) : paidStudents.map(item => (
                    <tr key={item._id}>
                      <td>
                        <div className="db-student-cell">
                          <div className="db-mini-avatar">{(getPaidStudentName(item)?.[0] || '?').toUpperCase()}</div>
                          <span style={{ fontWeight: 500 }}>{getPaidStudentName(item)}</span>
                        </div>
                      </td>
                      <td style={{ color: '#4B5563' }}>{getPaidStudentEmail(item)}</td>
                      <td><span className="db-badge db-badge-gold">{getPaymentStatusLabel(item)}</span></td>
                      <td><button className="db-action-btn" onClick={() => navigate('/admin/students')}>Details →</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── Two-column: Recent Enrollments + Widgets ── */}
        <div className="db-two-col">

          {/* Left — Recent Enrollments */}
          <div>
            <div className="db-section-head">
              <div className="db-section-title">
                <span className="db-section-dot" style={{ background: '#8B5CF6', boxShadow: '0 0 8px #8B5CF6' }} />
                Recent Enrollments
              </div>
              <button className="db-view-all" onClick={() => navigate('/admin/enrollments')}>View All →</button>
            </div>
            <div className="db-table-wrap">
              <div className="db-table-scroll">
                <table className="db-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Course</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan="4"><div className="db-loading"><div className="db-loading-dot"/><div className="db-loading-dot"/><div className="db-loading-dot"/></div></td></tr>
                    ) : recentEnrollments.length === 0 ? (
                      <tr><td colSpan="4"><div className="db-empty"><span className="db-empty-icon">📋</span>No enrollments yet.</div></td></tr>
                    ) : recentEnrollments.map((e, i) => {
                      const st = getStatus(e);
                      return (
                        <tr key={e._id || i}>
                          <td>
                            <div className="db-student-cell">
                              <div className="db-mini-avatar">{(getStudentName(e)?.[0] || '?').toUpperCase()}</div>
                              <span style={{ fontWeight: 500 }}>{getStudentName(e)}</span>
                            </div>
                          </td>
                          <td style={{ color: '#4B5563' }}>{getCourseName(e)}</td>
                          <td>
                            <span className={`db-badge ${st.type === 'new' ? 'db-badge-new' : 'db-badge-blue'}`}>{st.label}</span>
                          </td>
                          <td><button className="db-action-btn" onClick={() => navigate('/admin/enrollments')}>Details →</button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right — Widgets */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* Calendar / Schedule */}
            <div className="db-widget">
              <div className="db-section-head" style={{ marginBottom: '12px' }}>
                <div className="db-section-title" style={{ fontSize: '14px' }}>Schedule</div>
                <span style={{ fontSize: '11px', color: '#4B5563', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {now.toLocaleString('default', { month: 'short', year: 'numeric' })}
                </span>
              </div>
              <div className="db-cal-days">
                {calDays.map((d, i) => (
                  <div key={i} className={`db-cal-day${d.isToday ? ' today' : ''}`}>
                    <span className="db-cal-num">{d.num}</span>
                    <span className="db-cal-lbl">{d.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* New Users */}
            <div className="db-widget">
              <div className="db-section-head" style={{ marginBottom: '10px' }}>
                <div className="db-section-title" style={{ fontSize: '14px' }}>New Users</div>
                <button className="db-view-all" style={{ fontSize: '11px', padding: '4px 10px' }}>All</button>
              </div>
              <div className="db-people-list">
                {users.slice(0, 5).map(user => (
                  <div key={user._id} className="db-person-item">
                    <div className="db-person-av">{user.username?.[0]?.toUpperCase()}</div>
                    <div className="db-person-info">
                      <div className="db-person-name">{user.username}</div>
                      <div className="db-person-role">{user.role === 'admin' ? 'Administrator' : 'New Student'}</div>
                    </div>
                    <div className="db-person-actions">
                      <button className="db-tiny-btn">💬</button>
                      <button className="db-tiny-btn">👁️</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Upcoming Classes */}
            <div className="db-widget">
              <div className="db-section-head" style={{ marginBottom: '10px' }}>
                <div className="db-section-title" style={{ fontSize: '14px' }}>Upcoming Classes</div>
                <button className="db-view-all" style={{ fontSize: '11px', padding: '4px 10px' }}>All</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {users.slice(0, 3).map(user => (
                  <div key={user._id} className="db-upcoming-item">
                    <div className="db-upcoming-av">{user.username?.[0]?.toUpperCase()}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '2px', color: '#E2E8F0' }}>{user.username}</div>
                      <div style={{ fontSize: '11px', color: '#4B5563' }}>Instructor</div>
                    </div>
                    <span className="db-today-chip">Today</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>

      </div>
    </TrainHubLayout>
  );
};

export default Dashboard;